import { useEffect, useRef, useState } from 'react';

// Module-level caches keyed by `${audience}|${sorted,comma-separated scopes}`
const tokenCache: Record<string, string | null> = {};
const fetchPromises: Record<string, Promise<string | null>> = {};

/**
 * Fetches the JWT token from the server once and caches it in-memory.
 * Returns the token, loading state, error, and a refresh function.
 * Token is kept in memory only (not persisted) for security.
 */
export function useJwt(scopes: string[], audience: string) {
    if (!Array.isArray(scopes) || scopes.length === 0) {
        throw new Error('useJwt requires a non-empty scopes array.');
    }

    if (!audience) {
        throw new Error('useJwt requires a non-empty audience.');
    }

    const scopesPart = scopes.slice().sort().join(',');
    const cacheKey = `${audience}|${scopesPart}`;
    const hasCached = Object.prototype.hasOwnProperty.call(
        tokenCache,
        cacheKey,
    );
    const [jwt, setJwt] = useState<string | null>(
        hasCached ? (tokenCache[cacheKey] ?? null) : null,
    );
    const [loading, setLoading] = useState<boolean>(!hasCached);
    const [error, setError] = useState<Error | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (Object.prototype.hasOwnProperty.call(tokenCache, cacheKey)) {
            setJwt(tokenCache[cacheKey] ?? null);
            setLoading(false);
            return;
        }

        let isMounted = true;
        const controller = new AbortController();
        abortRef.current = controller;

        const run = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!fetchPromises[cacheKey]) {
                    const params = new URLSearchParams();
                    params.set('scopes', scopesPart);
                    params.set('audience', audience);
                    const search = params.toString();
                    fetchPromises[cacheKey] = fetch(
                        `/remix-api/user/jwt?${search}`,
                        { signal: controller.signal, credentials: 'include' },
                    )
                        .then(async (res) => {
                            if (!res.ok) {
                                throw new Error(
                                    `Failed to get JWT: ${res.status}`,
                                );
                            }
                            const data = await res.json();
                            const token: string =
                                data?.data?.JWTTokenCreate?.Token ?? '';
                            return token || null;
                        })
                        .catch((err) => {
                            // propagate error but do not cache failed state
                            throw err;
                        });
                }

                const token = await fetchPromises[cacheKey];
                if (isMounted) {
                    const finalToken = token ?? null;
                    tokenCache[cacheKey] = finalToken;
                    setJwt(finalToken);
                }
            } catch (err) {
                if (!isMounted) return;
                if ((err as Error)?.name === 'AbortError') return;
                setError(err as Error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        run();

        return () => {
            isMounted = false;
            abortRef.current?.abort();
        };
    }, [cacheKey, audience, scopesPart]);

    const refresh = async () => {
        delete tokenCache[cacheKey];
        delete fetchPromises[cacheKey];
        setJwt(null);
        setLoading(true);
        setError(null);

        // Trigger refetch by rerunning effect-like logic
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const params = new URLSearchParams();
            params.set('scopes', scopesPart);
            params.set('audience', audience);
            const search = params.toString();
            fetchPromises[cacheKey] = fetch(`/remix-api/user/jwt?${search}`, {
                signal: controller.signal,
                credentials: 'include',
            })
                .then(async (res) => {
                    if (!res.ok) {
                        throw new Error(`Failed to get JWT: ${res.status}`);
                    }
                    const data = await res.json();
                    const token: string =
                        data?.data?.JWTTokenCreate?.Token ?? '';
                    return token || null;
                })
                .catch((err) => {
                    throw err;
                });

            const token = await fetchPromises[cacheKey];
            const finalToken = token ?? null;
            tokenCache[cacheKey] = finalToken;
            setJwt(finalToken);
        } catch (err) {
            if ((err as Error)?.name === 'AbortError') return;
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    };

    return { jwt, loading, error, refresh } as const;
}
